import Alpine from 'alpinejs';
import Plyr from '../../vendor/plyr';
import { recItem } from './items.js';

export function getNarrativeManager(data) {
	let priv = Alpine.reactive({
		current: 0
	});

	const manager = {
		slides: data.slides.map((s_data) => getSlideManager(s_data)),
		exit_url: data.exit_url,
		get current() {
			return priv.current;
		},
		set current(i) {
			if (i >= 0 && i < this.slides.length) {
				this.current_slide.leave();
				priv.current = i;
				this.current_slide.enter();
			}
		},
		get current_slide() {
			return this.slides[this.current];
		},
		render_line_jumps(text) {
			return text.replace(/\r?\n|\r/g, '<br/>')
		},
		prev() {
			this.current--;
		},
		next() {
			this.current++;
		},
		has_prev() {
			return this.current > 0;
		},
		has_next() {
			return (this.current + 1) < this.slides.length;
		},
		go_to(i) {
			this.current = i;
		},
		get_color_styles() {
			const current_slide = this.current_slide;
			return { ...this.color_styles, ...current_slide.color_styles };
		},
		get_media_content_styles(media_data) {
			const styles = {};
			const { width, height } = media_data;
			if (width || height) {
				styles.width = width ? width + 'px' : 'auto';
				styles.height = height ? height + 'px' : 'auto';
			}
			return styles;
		}
	}

	const default_styles = {
		'--slide-bg': 'inherit',
		'--slide-color': 'inherit',
	};

	manager.color_styles = { ...default_styles, ...get_color_styles(data) };

	return manager;
}

export function getSlideManager(data) {
	let video_player;
	const playable = [];
	let autoplay = null;

	const manager = {
		current_extra: window.innerWidth <= 640 ? false : data.open,
		color_styles: get_color_styles(data),
		video_is_open: false,
		show_extra(extra_name) {
			this.current_extra = extra_name;
		},
		hide_extra() {
			this.current_extra = false;
		},
		is_open(extra_name) {
			return this.current_extra ? this.current_extra === extra_name : false;
		},
		open_video(video_data) {

			if (!video_player) {
				video_player = new Plyr(this.video_element, {
					controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
					youtube: { noCookie: true },
					vimeo: { dnt: true },
				});
			}

			this.current_video = video_data;

			video_player.source = {
				type: 'video',
				sources: [
					{
						src: video_data.src,
						provider: video_data.provider,
					}
				],
			};

			video_player.start = video_data.start;
			video_player.end = video_data.end;

			this.video_is_open = true;

			video_player.on('ready', () => {
				video_player.play();
			});
		},
		close_video() {
			video_player && video_player.stop();
			this.video_is_open = false;
		},
		set_video_element(element) {
			this.video_element = element;
		},
		register_playable(player) {
			playable.push(player);
			if (player.autoplay) {
				player.autoplay = false;

				if (!autoplay) {
					autoplay = player;
				}
			}
		},
		enter() {
			if (autoplay?.ready) {
				autoplay.play();
			}
			else {
				const queued = autoplay;
				autoplay?.on('ready', () => {
					queued.play()
				})
			}
			autoplay = null;
		},
		leave() {
			for (const player of playable) {
				if (player.playing) {
					player.pause();
					autoplay = player;
				}
			}
		},
		get is_media() {
			return this.type === 'media' || this.type === 'work';
		},
		get is_text() {
			return this.type === 'text';
		},
		get is_list() {
			return this.type === 'list';
		},
		get is_playlist() {
			return this.type === 'playlist';
		},
		get is_full_media() {
			return this.is_playlist || (this.media && this.media.length === 1);
		},
	};

	Object.assign(manager, data);

	if (manager.recs) {
		manager.recs = manager.recs.map((rec) => recItem(rec, manager));
	}

	if (manager.content) {
		manager.content = filter_user_content(manager.content);
	}

	if (manager.media) {
		for (const m of manager.media) {
			m.content = filter_user_content(m.content);
		}
	}

	if (manager.refs) {
		manager.refs = filter_user_content(manager.refs);
	}

	return manager;
}

function get_color_styles(data) {
	const styles = {};

	if (data.bg_color) {
		styles['--slide-bg'] = data.bg_color;
	}

	if (data.text_color) {
		styles['--slide-color'] = data.text_color;
	}

	return styles;
}

function filter_user_content(content) {
	const container = document.createElement('div');
	container.innerHTML = content;
	const links = container.getElementsByTagName('a');

	for (const link of links) {
		link.target = '_blank';
	}

	return container.innerHTML;
}